import React from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import { MatchTooltipContent } from "../matchState";
import "./MatchTooltipBody.scss";

// AB#35087: "How this match was made" panel (master-demo-v6). The MUI tooltip
// wrapper is made transparent/padding-less so ALL the visual styling lives on the
// inner .qe-match-tooltip container (MatchTooltipBody.scss) - which lets it be
// THEME-AWARE: white + blue accents in light mode (the app's LightTooltip
// convention) and the demo's dark slate in dark mode (body.dark-theme). Styling on
// the JSS class alone couldn't respond to the theme class the way an SCSS rule can.
export const MatchInfoTooltip = withStyles(() => ({
    tooltip: {
        backgroundColor: "transparent",
        padding: 0,
        margin: 0,
        maxWidth: "none",
        color: "inherit",
        fontSize: "inherit",
    },
}))(Tooltip);

// Renders `text` with each `terms` substring wrapped in a bold <strong> (the
// mockup bolds the dynamic search value, e.g. the fax number). Pure display.
function renderWithEmphasis(text: string, terms?: readonly string[]): React.ReactNode {
    const present = (terms ?? []).filter(Boolean);
    if (present.length === 0) {
        return text;
    }
    const escaped = present.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    // Word-boundary-anchor so a SHORT search term (e.g. a two-letter last name)
    // can't bold a spurious substring inside another word. The emphasize terms are
    // last name / DOB / fax / email, which all start and end on a word character,
    // so \b matches their real edges (regression review nit).
    const re = new RegExp(`\\b(${escaped.join("|")})\\b`, "g");
    return text.split(re).map((seg, i) =>
        present.includes(seg)
            ? <strong key={i} className="qe-match-tooltip__value">{seg}</strong>
            : <React.Fragment key={i}>{seg}</React.Fragment>,
    );
}

const MatchTooltipBody: React.FC<{ content: MatchTooltipContent }> = ({ content }) => (
    <div className="qe-match-tooltip">
        <div className="qe-match-tooltip__header">{content.header}</div>
        <div className="qe-match-tooltip__line">{renderWithEmphasis(content.searchLine, content.emphasize)}</div>
        {content.rules && content.rules.length ? (
            <ul className="qe-match-tooltip__rules">
                {content.rules.map((rule, i) => (
                    <li key={i} className={i === content.activeRuleIndex ? "is-active" : undefined}>{`${i + 1} · ${rule}`}</li>
                ))}
            </ul>
        ) : null}
    </div>
);

export default MatchTooltipBody;
